import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";

import { V1, V2 } from "../../Api";
import { formatName, formatDateTimeLocalNumeric } from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import HasRight from "../HasRight";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../table.css";
import "../viewer.css";

interface IQuestionnaireContactListViewState {
    appointments: any[];

    locations: any[];
    //searchText: string;
    //searchLocation: string;
    searchOffset: number;
    searchPerson: string;
    searchStatus: string;
    searchQuestion: string;

    searchMonth: string;

    isLoading: boolean | string;

    marketers: any[];
    questions: any[];
}

const PAGE_SIZE = 50;

class QuestionnaireContactListView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, IQuestionnaireContactListViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        const targetDate = DateTime.local().startOf("month");

        this.state = {
            appointments: [],

            locations: [],
            //searchLocation: props.location.state ? props.location.state.searchLocation : "",
            searchOffset: props.location.state && props.location.state.searchOffset ? props.location.state.searchOffset : 0,
            searchPerson: (props.location.state && props.location.state.searchPerson) || (this.props.authContext.isInGroup(WellKnownGroups.Marketer) ? "caller" : ""),
            searchStatus: props.location.state ? props.location.state.searchStatus : "",
            searchQuestion: props.location.state ? props.location.state.searchQuestion : "",
            //searchText: props.location.state ? props.location.state.searchText : "",

            searchMonth: props.location.state ? props.location.state.searchMonth : targetDate.toFormat("yyyy-MM"),

            isLoading: true,

            marketers: [],
            questions: [],
        };

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public async componentDidMount() {
        try {
            const [marketers, questions] = await Promise.all([
                V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`),
                V2.get<any[]>("survey-types/questions"),
            ]);

            this.setState({
                marketers: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),
                questions: questions.sort((a, b) => a.text.localeCompare(b.text))
            });

            await this.search();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load data"
            });
        }
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.appointments.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={10}>No appointments found</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.appointments.map(a => (
                    <tr key={a.id} className={this.statusToClass(a.contactInfo.status, a.hasAppointments, a.hasProof)}>
                        <td className={"inline " + this.statusToFlagClass(a.contactInfo.status, a.hasAppointments, a.hasProof)} />
                        <td className="inline" data-caption="Submitted: ">{formatDateTimeLocalNumeric(a.timestamp)}</td>
                        <td className="inline wrap" data-caption="Name: ">{formatName(a.name)}</td>
                        <td className="inline wrap" data-caption="Fair: ">{a.healthFair.name}</td>
                        <td className="inline wrap" data-caption="Insurance: ">{a.insurance.provider || "Not provided"}</td>
                        <td className="inline col-center" data-caption="Sex: ">{a.sex || "-"}</td>
                        <td>
                            <Link className="button small" to={`/surveys/${a.id}`}>View</Link>
                        </td>
                    </tr>
                ));
            }

            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest" />
                            <th className="col-smallest">Submitted</th>
                            <th>Name</th>
                            <th>Fair</th>
                            <th>Insurance</th>
                            <th className="col-smallest">Sex</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Questionnaires</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <div className="filter-control-box">
                    <div className="control-group">
                        <span>Date:</span>
                        <DelayInput type="month" timeout={500} value={this.state.searchMonth} name="searchMonth" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <span className="fill-space" />
                    <select className="control-max-200px" value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Status</option>
                        <option value="notreached">Not Reached</option>
                        <option value="reached">Reached, Nothing wanted</option>
                        <option value="reachedcontactlater">Reached, Wants contact later</option>
                        <option value="gotappointment">Reached, Got Appointment</option>
                        <option value="donotcontact">Do Not Contact</option>
                        <option value="surgmedcontact">Surg med Reached</option>
                        <option value="surgmedappt">Surg med Appointment</option>
                        <HasRight right="survey:contact:exflt">
                            <option value="gotappointment_noproof">Has Appointment, No Proof</option>
                            <option value="gotappointment_wrongstatus">Has Appointment, Wrong Status</option>
                            <option value="gotappointment_all">Has Appointment, All</option>
                        </HasRight>
                    </select>
                    <select className="control-max-200px" value={this.state.searchQuestion} name="searchQuestion" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Questions</option>
                        {this.state.questions.map(a => <option key={a.id} value={a.id}>{a.text}</option>)}
                    </select>
                    <select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <HasRight right="appt:readall">
                            <option value="">Everyone</option>
                        </HasRight>
                        <option value="caller">You</option>
                        <HasRight right="appt:readall">
                            {
                                this.state.marketers.map(a =>
                                    <option key={a.id} value={a.id}>{formatName(a.name)}</option>
                                )
                            }
                        </HasRight>
                    </select>
                </div>
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.appointments.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`,
                "mode=contact",
                "sort=asc",
                `sort_by=date_for`
            ];
            //if (this.state.searchText) {
            //    parameters.push(`q=${this.state.searchText}`);
            //}
            if (this.state.searchQuestion) {
                parameters.push(`question=${this.state.searchQuestion}`);
            }
            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchMonth) {
                const afterDt = DateTime.fromISO(this.state.searchMonth).startOf("month");
                const beforeDt = afterDt.endOf("month").plus({ millisecond: 1 });
                parameters.push(`after=${afterDt.toISODate()}`);
                parameters.push(`before=${beforeDt.toISODate()}`);
            }

            const results = await V1.get<any[]>(`survey/search?${parameters.join("&")}`);
            for (const item of results) {
                item.contactInfo = item.contactInfo || {};
            }

            this.setState({
                appointments: results,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private statusToClass(status: number, hasAppointments: boolean, hasProof: boolean) {
        if ((hasAppointments && !hasProof) || (hasAppointments && status !== 3)) {
            return "row-green row-flag";
        }
        switch (status) {
            case 7: return "row-orange";
            case 6: return "row-purple";
            case 5: return "row-blue";
            case 4: return "row-gray-dark";
            case 3: return "row-green";
            case 2: return "row-yellow";
            case 1: return "row-red";
            default: return "";
        }
    }

    private statusToFlagClass(status: number, hasAppointments: boolean, hasProof: boolean) {
        if (!!status && status !== 3 && hasAppointments)
            return "col-flag-yellow";

        return "col-flag-red";
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            //searchLocation: this.state.searchLocation,
            searchOffset: this.state.searchOffset,
            searchPerson: this.state.searchPerson,
            searchStatus: this.state.searchStatus,
            searchQuestion: this.state.searchQuestion,
            //searchText: this.state.searchText,

            searchMonth: this.state.searchMonth,
        };

        this.props.history.replace(location);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }
}

export default withAuth(withTitle("Survey Outreach List", QuestionnaireContactListView));
